import { CombinedFlows, TransactionTypes } from "./flow-types";
import { isBehalfPanelValid } from "./participant-list-builder";

export function buildPullParticipantList(dataElement, identity) {
	if (!dataElement?.from) {
		return [];
	}

	// Group participants by ID to detect duplicates
	const participantMap = new Map();

	dataElement.from.forEach((element) => {
		const participantId = element.id;

		if (!participantMap.has(participantId)) {
			participantMap.set(participantId, {
				base: element,
				hasCOBOP: false,
				hasCOB: false,
				hasConsumer: false,
				cobopEntry: null,
				cobEntry: null,
				consumerEntry: null,
			});
		}

		const entry = participantMap.get(participantId);

		// Check if this is a COBOP entry
		if (element.for_principals && element.for_principals.length > 0) {
			entry.hasCOBOP = true;
			entry.cobopEntry = element;
		}

		// Check if this is a COB entry (has other orgs in on_behalf_of)
		if (element.on_behalf_of && element.on_behalf_of.length > 0) {
			const hasOtherOrgs = element.on_behalf_of.some(
				(org) => org.id !== identity?.organization?.id
			);
			const hasCurrentOrg = element.on_behalf_of.some(
				(org) => org.id === identity?.organization?.id
			);

			if (hasOtherOrgs && !element.for_principals) {
				entry.hasCOB = true;
				entry.cobEntry = element;
			}

			if (hasCurrentOrg && !element.for_principals) {
				entry.hasConsumer = true;
				entry.consumerEntry = element;
			}
		}
	});

	const participantList = [];

	participantMap.forEach((entry, participantId) => {
		const {
			base,
			hasCOBOP,
			hasCOB,
			hasConsumer,
			cobopEntry,
			cobEntry,
			consumerEntry,
		} = entry;

		let mergedType = null;
		let on_behalf_of_list = [];

		if (hasCOBOP && hasCOB && hasConsumer) {
			mergedType = CombinedFlows.CONSUMER_COB_COBOP;
			on_behalf_of_list = consumerEntry?.on_behalf_of || cobEntry?.on_behalf_of || [];
		} else if (hasCOBOP && hasCOB && !hasConsumer) {
			mergedType = CombinedFlows.COB_COBOP;
			on_behalf_of_list = cobEntry?.on_behalf_of || [];
		} else if (hasCOBOP && hasConsumer && !hasCOB) {
			mergedType = CombinedFlows.CONSUMER_COBOP;
			on_behalf_of_list = consumerEntry?.on_behalf_of || [];
		}

		if (mergedType) {
			participantList.push({
				...base,
				id: participantId,
				isMerged: true,
				mergedType: mergedType,
				hasCOBOP: hasCOBOP,
				hasCOB: hasCOB,
				hasConsumer: hasConsumer,
				for_principals: cobopEntry?.for_principals || [],
				on_behalf_of: on_behalf_of_list,
				systems: base.systems,
			});
		}
		// Only COBOP
		else if (hasCOBOP) {
			participantList.push({
				...cobopEntry,
				id: `${participantId}_COBOP`,
				originalId: participantId,
				isCOBOP: true,
				for_principals: cobopEntry.for_principals || [],
			});
		}
		// Only COB
		else if (hasCOB) {
			participantList.push({
				...cobEntry,
				id: participantId,
				on_behalf_of: cobEntry.on_behalf_of || [],
			});
		}
		// Only Consumer
		else if (hasConsumer) {
			participantList.push({
				...consumerEntry,
				id: participantId,
				on_behalf_of: consumerEntry.on_behalf_of || [],
			});
		}
		// Regular participant
		else {
			participantList.push({
				...base,
				id: participantId,
			});
		}
	});

	return participantList;
}

export function addPullAgentParticipants(participantList, dataElement) {
	const agentParticipants =
		dataElement?.from_agents?.map((element) => ({
			...element,
			id: `${element.id}_AGENT`,
			originalId: element.id,
			isAgent: true,
			to_principals: element.to_principals || [],
			on_behalf_of: element.on_behalf_of || [],
		})) || [];

	return [...participantList, ...agentParticipants];
}

function validatePullFromAgent(
	recipients,
	onBehalf,
	selectedPrincipals,
	behalfPanel
) {
	const isValidAgent = Boolean(recipients[0]?.isAgent);
	const hasPrincipals = selectedPrincipals.length > 0;
	const isBehalfValid = isBehalfPanelValid(behalfPanel, onBehalf);
	return isValidAgent && hasPrincipals && isBehalfValid;
}

function validatePullFromMergedParticipant(
	recipients,
	onBehalf,
	selectedPrincipals,
	transactionType
) {
	const mergedType = recipients[0]?.mergedType;

	if (shouldApplyOrLogicForPull(recipients, transactionType)) {
		return onBehalf.length > 0 || selectedPrincipals.length > 0;
	}

	if (mergedType === CombinedFlows.CONSUMER_COBOP) {
		return true;
	}

	return onBehalf.length > 0 || selectedPrincipals.length > 0;
}

export function validatePullParticipantSelection(
	isAgentPrincipalUiPullEnabled,
	recipients,
	onBehalf,
	selectedPrincipals,
	behalfPanel,
	transactionType
) {
	if (!isAgentPrincipalUiPullEnabled) {
		return isBehalfPanelValid(behalfPanel, onBehalf);
	}

	if (transactionType === TransactionTypes.PULL_FROM_AGENT) {
		return validatePullFromAgent(
			recipients,
			onBehalf,
			selectedPrincipals,
			behalfPanel
		);
	}

	const isMergedParticipant = recipients[0]?.isMerged;
	const isCOBOP = recipients[0]?.isCOBOP;

	if (isMergedParticipant) {
		return validatePullFromMergedParticipant(
			recipients,
			onBehalf,
			selectedPrincipals,
			transactionType
		);
	}

	// if only isCOBOP and not a merged participant
	if (isCOBOP) {
		return selectedPrincipals.length > 0;
	}

	return isBehalfPanelValid(behalfPanel, onBehalf);
}

export function handlePullRecipientChangeLogic(
	isAgentPrincipalUiPullEnabled,
	option,
	currentRecipient
) {
	if (!isAgentPrincipalUiPullEnabled) {
		return {
			principalsList: [],
			selectedPrincipals: [],
		};
	}

	// Handle merged participants (COBOP + COB)
	if (option?.[0]?.isMerged) {
		const myPrincipalsList = option[0].for_principals.map((principal) => ({
			id: principal.uen || principal.name,
			name: principal.name,
			uen: principal.uen,
		}));

		return {
			principalsList: myPrincipalsList,
			selectedPrincipals: [],
		};
	}
	// Handle Agent recipients
	else if (option?.[0]?.isAgent && option[0]?.to_principals) {
		const newPrincipalsList = option[0].to_principals.map((principal) => ({
			id: principal.uen || principal.name,
			name: principal.name,
			uen: principal.uen,
			systemIds: principal.systemIds,
			forOrgs: principal.forOrgs || [],
		}));

		// Only clear selectedPrincipals if switching to a different agent
		const shouldClearSelected = !currentRecipient || currentRecipient[0]?.id !== option[0]?.id;

		return {
			principalsList: newPrincipalsList,
			selectedPrincipals: shouldClearSelected ? [] : undefined, // undefined means don't update
		};
	}
	// Handle COBOP recipients
	else if (option?.[0]?.isCOBOP && option[0]?.for_principals) {
		const myPrincipalsList = option[0].for_principals.map((principal) => ({
			id: principal.uen || principal.name,
			name: principal.name,
			uen: principal.uen,
		}));

		// Only clear selectedPrincipals if switching to a different POBOP
		const shouldClearSelected = !currentRecipient || currentRecipient[0]?.id !== option[0]?.id;

		return {
			principalsList: myPrincipalsList,
			selectedPrincipals: shouldClearSelected ? [] : undefined, // undefined means don't update
		};
	}
	// Reset when not applicable
	else {
		return {
			principalsList: [],
			selectedPrincipals: [],
		};
	}
}

export function getAvailableTransactionTypesForPull(availableRoles) {
	return {
		hasPull: availableRoles?.hasConsumerFromContributor || availableRoles?.hasCOB || availableRoles?.hasCOBOP,
		hasPullFromAgent: availableRoles?.hasConsumerFromAgent,
	};
}

export function filterParticipantsByTransactionTypeForPull(
	participantList,
	transactionType
) {
	if (!transactionType) return participantList;

	return participantList.filter((p) => {
		if (transactionType === TransactionTypes.PULL_FROM_AGENT) {
			return p.isAgent; // Only agents
		} else {
			return !p.isAgent; // Only non-agents
		}
	});
}

export function shouldApplyOrLogicForPull(recipient, transactionType) {
	// if not of merged type or not a valid transaction type (PULL), do not apply OR logic
	if (!recipient?.[0]?.isMerged || transactionType !== TransactionTypes.PULL) {
		return false;
	}

	// OR logic only valid when merged type is COB_COBOP or CONSUMER_COB_COBOP
	const mergedType = recipient[0].mergedType;
	return mergedType === CombinedFlows.COB_COBOP || mergedType === CombinedFlows.CONSUMER_COB_COBOP;
}

// Calculate the intersection of forOrgs arrays from selected principals
// Returns the common organizations across all selected principals
export function calculateValidOrgsForPrincipals(selectedPrincipals) {
	if (!selectedPrincipals || selectedPrincipals.length === 0) {
		return [];
	}

	let validOrgs = selectedPrincipals[0].forOrgs || [];

	for (let i = 1; i < selectedPrincipals.length; i++) {
		const currentForOrgs = selectedPrincipals[i].forOrgs || [];
		validOrgs = validOrgs.filter((org) => currentForOrgs.includes(org));
	}

	return validOrgs;
}

// Check if a principal can be selected based on current valid orgs
export function isPrincipalSelectable(principal, selectedPrincipals) {
	if (!selectedPrincipals || selectedPrincipals.length === 0) {
		return true;
	}

	// Calculate current valid orgs
	const validOrgs = calculateValidOrgsForPrincipals(selectedPrincipals);

	if (validOrgs.length === 0) {
		return false;
	}

	// Check if this principal has at least one org in common with valid orgs
	const principalForOrgs = principal.forOrgs || [];
	return principalForOrgs.some((org) => validOrgs.includes(org));
}

// Filter on_behalf_of list based on valid orgs from selected principals
export function filterOnBehalfOfByPrincipals(
	onBehalfOfList,
	selectedPrincipals
) {
	if (!selectedPrincipals || selectedPrincipals.length === 0) {
		return onBehalfOfList;
	}

	const validOrgs = calculateValidOrgsForPrincipals(selectedPrincipals);

	if (validOrgs.length === 0) {
		return [];
	}

	return onBehalfOfList.filter((org) => validOrgs.includes(org.id));
}
